import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {NetscopeUiService} from '@app/services/netscope-ui.service';

/**
 * How the theme is chosen:
 *  - 'system': follows the browser (which itself follows the operating system)
 *  - 'light' / 'dark': always that theme
 *  - 'schedule': light theme between two times of the day, dark theme the rest of the time
 */
export type ThemeMode = 'system' | 'light' | 'dark' | 'schedule';

/** The automatic switch: times are in minutes since midnight, in the browser's time zone. */
export interface ThemeSchedule {
  lightAt: number;
  darkAt: number;
}

export const MINUTES_IN_A_DAY = 1440;
/** The light period lasts at least this long, so the two handles of the slider never overlap */
export const MINIMUM_LIGHT_PERIOD = 60;
/** Latest time a handle can take: 23:45 (24:00 cannot be typed in a time field) */
export const LATEST_SCHEDULE_TIME = MINUTES_IN_A_DAY - 15;

const MODE_STORAGE_KEY = 'netscope.theme.mode';
const SCHEDULE_STORAGE_KEY = 'netscope.theme.schedule';
const DEFAULT_SCHEDULE: ThemeSchedule = {lightAt: 7 * 60, darkAt: 19 * 60};
/** How often the schedule is checked, in milliseconds */
const SCHEDULE_CHECK_INTERVAL = 30000;

/**
 * Applies the light or dark theme and remembers the choice in the browser (localStorage), as there is no
 * user account to store it with. The theme is applied by setting the 'cds-theme' attribute of the page body
 * (Clarity styles) and by telling NetscopeUiService, which the charts listen to.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  /** true when the light theme is shown, whatever the mode that chose it */
  public isLightTheme$ = new BehaviorSubject<boolean>(true);
  public mode$ = new BehaviorSubject<ThemeMode>('system');
  public schedule$ = new BehaviorSubject<ThemeSchedule>(DEFAULT_SCHEDULE);

  private systemDarkQuery: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

  constructor(private netscopeUiService: NetscopeUiService) {
    this.mode$.next(this.readStoredMode());
    this.schedule$.next(this.readStoredSchedule());

    // The browser theme can change while the page is open (for instance an automatic dark mode at night)
    this.systemDarkQuery.addEventListener('change', () => this.applyTheme());

    // In 'schedule' mode, the theme has to change by itself when a switch time is reached
    setInterval(() => this.applyTheme(), SCHEDULE_CHECK_INTERVAL);

    this.applyTheme();
  }

  setMode(mode: ThemeMode) {
    this.mode$.next(mode);
    this.writeStorage(MODE_STORAGE_KEY, mode);
    this.applyTheme();
  }

  /** Saves the schedule and switches to the 'schedule' mode */
  setSchedule(schedule: ThemeSchedule) {
    this.schedule$.next(schedule);
    this.writeStorage(SCHEDULE_STORAGE_KEY, JSON.stringify(schedule));
    this.setMode('schedule');
  }

  /** The theme the browser asks for, used by the 'system' mode */
  isSystemThemeLight(): boolean {
    return !this.systemDarkQuery.matches;
  }

  /** Is the light theme due at this time of the day (in minutes since midnight)? */
  isLightTimeInSchedule(minutes: number, schedule: ThemeSchedule): boolean {
    return minutes >= schedule.lightAt && minutes < schedule.darkAt;
  }

  private applyTheme() {
    const isLight = this.computeIsLight();

    // Nothing to do when the theme does not change (this runs every 30 seconds). The page starts in the
    // light theme (index.html), which is the initial value of isLightTheme$.
    if (isLight === this.isLightTheme$.value) {
      return;
    }

    document.body.setAttribute('cds-theme', isLight ? 'light' : 'dark');
    this.isLightTheme$.next(isLight);
    this.netscopeUiService.setTheme(isLight);
  }

  private computeIsLight(): boolean {
    const mode = this.mode$.value;
    if (mode === 'light') {
      return true;
    }
    if (mode === 'dark') {
      return false;
    }
    if (mode === 'schedule') {
      const now = new Date();
      const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
      return this.isLightTimeInSchedule(minutesSinceMidnight, this.schedule$.value);
    }
    return this.isSystemThemeLight();
  }

  private readStoredMode(): ThemeMode {
    const stored = this.readStorage(MODE_STORAGE_KEY);
    const knownModes: ThemeMode[] = ['system', 'light', 'dark', 'schedule'];
    if (knownModes.includes(stored as ThemeMode)) {
      return stored as ThemeMode;
    }
    return 'system';
  }

  private readStoredSchedule(): ThemeSchedule {
    const stored = this.readStorage(SCHEDULE_STORAGE_KEY);
    if (stored === null) {
      return DEFAULT_SCHEDULE;
    }
    try {
      const parsed = JSON.parse(stored);
      const lightAt = Number(parsed.lightAt);
      const darkAt = Number(parsed.darkAt);
      // A schedule edited by hand in the browser could be broken: fall back to the default one
      const isValid = lightAt >= 0 && darkAt <= LATEST_SCHEDULE_TIME && darkAt - lightAt >= MINIMUM_LIGHT_PERIOD;
      if (isValid) {
        return {lightAt: lightAt, darkAt: darkAt};
      }
    } catch (error) {
      console.log(`Ignoring the stored theme schedule: ${error}`);
    }
    return DEFAULT_SCHEDULE;
  }

  // localStorage can be unavailable (private browsing, blocked site data): the theme then works without memory
  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  private writeStorage(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.log(`Could not store the theme choice: ${error}`);
    }
  }
}
